/* eslint-disable dot-notation */
// import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DataSecurityMgr } from '../DataSecurityMgr';
import { sdkLoggerInterface as log } from '../../Logger/Logger';
const generateUniqueId = () => {
    return uuidv4();
};
/**
 * @class BCManager
 * The BC Manager is used to enable the data security that looks at the user behaviour on the page and
 * send back regular calls to the Security API to analyse the user usage data and detect fraudulent transactions.
 * The class is responsible to instantiate the library and set unique session ID.
 * This Session ID will be passed down to the miniapp.
 * The miniapp will be able to add this session ID in the headers for the API calls it makes to downstream system.
 *
 * @example
 * To set this up in a shell you'll need to know the Library URL,
 * So that when the AppLoader is loading and setting up the miniapp it is able to create the class and set the script tag up on the dom.
 *
 * **NOTE** The Shell only needs to follow the example below, as this initialisation happens within the AppLoader By default
 *
 * ```typescript
 * const bc = new BCManager("URL TO THE LIBRARY");
 * // this return the bio catch case id string now.
 * const bc: string = await bc.getData();
 * // sets the context for the bio catch calls
 * const response= bc.setContext('myContext')
 * if(response) {
 *   log.info('context set successfully')
 * } else {
 *   log.warn('context not set as the library is not initialised')
 * }
 * ```
 *
 * @example
 * Shell can configure the Bio Catch library via the the service configuration.
 * The configuration allow to set an library URL and an _optional_ config value that will allow then to re-use any existing **CSID**.
 * By default the CSID will be auto generated as unique GUID, if the config is not provided.
 * To enable this Library in your application.
 *
 * ```typescript
 * initiRuntime({
 *   ...
 *   userConfig: {
 *     type: 'customer',
 *     org: 'nab'
 *   },
 *   servicesConfig: {
 *     dataSecurity: {
 *       bc {
 *         LibraryUrl: 'https://bc.library.url',
 *
 *        // Optional configuration to send in any existing CSID <start>
 *        configValue: 'exiting-cs-id-value'
 *        // <end>
 *       }
 *     }
 *   }
 *   ...
 * })
 * ```
 * @example
 * Miniapp(s) can use this objects passed via the as `user` property to App.tsx, or via the MiniappContext
 * Irrespective of whether the library is passed or not, the class for BC will be passed down and accessible via the `await user.dataSecurity('bc').getData()` call,
 *  and can be used to see if the promise resolution is positive or failure and handle their case accordingly
 *
 * ```typescript
 * const App = function({ ..., user}) {
 *   ...
 *   let data = '';
 *   try{
 *      const bcFuncs = user.dataSecurity('bc');
 *      data  = await bcFuncs.getData();                        // returns the BC CSID value
 *
 *      // sets the context for the bio catch calls
 *      const response = bcFuncs.setContext('myContext')
 *      if(response) {
 *         log.info('context set successfully')
 *      } else {
 *        log.warn('context not set as the library is not initialised')
 *      }
 *   }
 *   catch(e) {
 *      warn('unable to get the data', e);
 *   }
 *
 *  // using the data.
 *   fetch({
 *     url: 'https://downstream.system.com/server/to/call',
 *     headers: {
 *       ...
 *       ...(data && { 'x-nab-custom-header-CSID': data })
 *     }
 *   })
 *   ...
 * }
 * ```
 */
class BCManager extends DataSecurityMgr {
    /**
     * Used to set the configuration and the library URI to load the unique ID for BC data.
     * @param cdnUrl Library URI to be loaded. Usually a CDN path.
     * @param uniqueID The unique ID provided to start the BC session, this allows shells to set their customized unique ID
     */
    constructor(cdnUrl, uniqueID) {
        super(cdnUrl, uniqueID);
        this.GLOBAL_BC_VAR_NAME = 'cdApi';
        this.CSID = '';
        this.initialise();
    }
    /**
     * Required to be initialised on load of the application.
     * This will load the BC library using the specified cdnURL.
     */
    async initialise() {
        const isScriptLoaded = await this.loadLibrary();
        if (isScriptLoaded) {
            this.setCSID();
            return true;
        }
        return false;
    }
    /**
     * Sets the unique session ID based on the uniqueID provided in the constructor.
     * If no custom unique ID is provided, a unique ID is generated using generateUniqueId()
     */
    setCSID() {
        this.CSID = this.config || generateUniqueId();
        if (window[this.GLOBAL_BC_VAR_NAME]) {
            window[this.GLOBAL_BC_VAR_NAME].setCustomerSessionId(this.CSID);
        }
    }
    /**
     * The data generated by the BC library can be retrieved here.
     * An error will be thrown the bc function is not found in the window or if no CSID is set
     */
    async getData() {
        if (window[this.GLOBAL_BC_VAR_NAME] && this.CSID) {
            return this.CSID;
        }
        return Promise.reject(new Error('Data security feature for BC not initialised.'));
    }
    /**
     * @function setContext
     * @description Use this function to set or change the context of the BioCatch fraud library set-up
     * @param context The string value that will be the used to set the name of the context.
     */
    setContext(context) {
        if (window[this.GLOBAL_BC_VAR_NAME] && this.CSID && typeof window[this.GLOBAL_BC_VAR_NAME].changeContext === 'function') {
            window[this.GLOBAL_BC_VAR_NAME].changeContext(context);
            return true;
        }
        log.warn('Data security library not initialised');
        return false;
    }
}
export { BCManager };
//# sourceMappingURL=BCManager.js.map